/**
 * `persistQueryClient` over IndexedDB (design §2.12).
 *
 *   "TanStack Query persists to IndexedDB (`persistQueryClient`) so a reload
 *    during a flaky connection shows cached data with a **'showing cached data
 *    from {time}'** banner."
 *
 * The banner is why this file writes its own `Persister` instead of using
 * `createAsyncStoragePersister`. That helper serialises `{ timestamp, buster,
 * clientState }` into an opaque string and hands `persistQueryClientRestore`
 * the parsed object; nothing outside the library ever sees the timestamp. The
 * banner's whole job is to name that timestamp, so this persister keeps it —
 * publishing `restoredAt` the moment a restore succeeds.
 *
 * ## What is deliberately NOT persisted
 *
 * `shouldDehydrateQuery` excludes:
 *
 *  - **the session** (`['session']`), because a cached "you are signed in" would
 *    let the authed layout render a shell for a session the server has since
 *    revoked. Auth state is asked, never remembered.
 *  - **anything that errored**, because replaying a failure from disk shows an
 *    error for a request this session never made.
 *
 * Everything else is org-prefixed (`keys.ts`), so a restored cache cannot leak
 * one tenant's rows into another's view even though one database serves both.
 *
 * `BUSTER` must change whenever a cached shape changes. It is tied to the client
 * version so a deploy that reshapes a response cannot hydrate the old shape into
 * the new components.
 */

import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import { CLIENT_VERSION } from '../env.ts';
import { idbDel, idbGet, idbSet } from './idb.ts';

const IDB_KEY = 'query-cache.v1';

/** Twelve hours. Older than that and "cached data from…" stops being useful. */
export const MAX_CACHE_AGE_MS = 12 * 60 * 60 * 1000;

export const BUSTER = `web:${CLIENT_VERSION}`;

// --- the restore timestamp, as a subscribable store --------------------------

let restoredAt: number | null = null;

/**
 * What the last `restoreClient()` handed back, pending the library's verdict.
 *
 * `persistQueryClientRestore` decides whether to keep it — it discards a busted
 * or over-age cache and calls `removeClient()` when it does. So the candidate is
 * set by the persister, cleared by `removeClient`, and only promoted to
 * `restoredAt` by `noteRestore`. Declared here, beside the store it feeds,
 * rather than next to the persister, so `resetRestoredAt` below is not reaching
 * forward into a temporal dead zone.
 */
let candidateTimestamp: number | null = null;

const restoreListeners = new Set<() => void>();

/** `null` until a restore has actually put something into the cache. */
export function getRestoredAt(): number | null {
  return restoredAt;
}

export function subscribeRestoredAt(listener: () => void): () => void {
  restoreListeners.add(listener);
  return () => restoreListeners.delete(listener);
}

function setRestoredAt(value: number | null): void {
  if (restoredAt === value) return;
  restoredAt = value;
  for (const listener of restoreListeners) listener();
}

/** Test seam; also used by sign-out, where a restored cache must not survive. */
export function resetRestoredAt(): void {
  candidateTimestamp = null;
  setRestoredAt(null);
}

// --- the persister -----------------------------------------------------------

function isPersistedClient(value: unknown): value is PersistedClient {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<PersistedClient>;
  return (
    typeof candidate.timestamp === 'number' &&
    typeof candidate.buster === 'string' &&
    typeof candidate.clientState === 'object' &&
    candidate.clientState !== null
  );
}

/**
 * Stores the `PersistedClient` as a STRUCTURED CLONE, not JSON.
 *
 * IndexedDB clones natively, so there is no serialise/parse round trip to get
 * wrong — and no risk of a `JSON.parse` throwing on a half-written record and
 * taking the boot path with it. A value that fails the shape check below is
 * discarded rather than hydrated.
 */
export function createIdbPersister(): Persister {
  return {
    persistClient: async (client) => {
      await idbSet(IDB_KEY, client);
    },
    restoreClient: async () => {
      const stored = await idbGet<unknown>(IDB_KEY);
      if (!isPersistedClient(stored)) {
        // Absent, unreadable, or written by a version whose shape we do not
        // recognise. Either way there is nothing safe to hydrate.
        candidateTimestamp = null;
        return undefined;
      }
      // A record whose dehydrated state holds NO queries restores nothing, so
      // the banner would be claiming cached data that does not exist.
      const queries = (stored.clientState as { queries?: readonly unknown[] }).queries;
      candidateTimestamp = Array.isArray(queries) && queries.length > 0 ? stored.timestamp : null;
      return stored;
    },
    removeClient: async () => {
      // Reached both on an explicit clear AND when the library rejects what
      // `restoreClient` returned (busted buster, over `maxAge`, hydrate threw).
      // Either way nothing was restored.
      candidateTimestamp = null;
      await idbDel(IDB_KEY);
      setRestoredAt(null);
    },
  };
}

export type PersistOptions = {
  readonly persister: Persister;
  readonly maxAge: number;
  readonly buster: string;
  readonly dehydrateOptions: {
    readonly shouldDehydrateQuery: (query: {
      readonly queryKey: readonly unknown[];
      readonly state: { readonly status: string };
    }) => boolean;
  };
};

/**
 * The options object handed to `<PersistQueryClientProvider persistOptions>`.
 *
 * Exported as a pure function so the exclusion rules above are a unit test
 * rather than a comment inside `main.tsx`.
 */
export function persistOptions(persister: Persister = createIdbPersister()): PersistOptions {
  return {
    persister,
    maxAge: MAX_CACHE_AGE_MS,
    buster: BUSTER,
    dehydrateOptions: { shouldDehydrateQuery },
  };
}

export function shouldDehydrateQuery(query: {
  readonly queryKey: readonly unknown[];
  readonly state: { readonly status: string };
}): boolean {
  if (query.state.status !== 'success') return false;
  return query.queryKey[0] !== 'session';
}

/**
 * Publishes the restore timestamp, or `null` when nothing was restored.
 *
 * Called from `<PersistQueryClientProvider onSuccess>`, which the library
 * invokes after `persistQueryClientRestore` settles and before it flips
 * `isRestoring` off.
 *
 * ## Why this reads a candidate rather than the cache
 *
 * The obvious test — "is the query cache non-empty?" — is wrong here. The
 * provider renders its children with `isRestoring: true` BEFORE running the
 * restore effect, so mounted `useQuery` hooks have already built (empty,
 * pending) entries in the cache by the time `onSuccess` fires. On a cold boot
 * with nothing persisted, that test would report a restore and the banner would
 * announce cached data that does not exist.
 *
 * The candidate is set by `restoreClient` and cleared by `removeClient`, which
 * is exactly the library's own accept/reject decision — including the buster
 * mismatch and the `maxAge` expiry, neither of which this module evaluates
 * itself.
 */
export function noteRestore(): void {
  setRestoredAt(candidateTimestamp);
}
